import * as React from "react"
import { AlertCircle } from "lucide-react"
import { Button } from "./button"
import { Alert, AlertDescription, AlertTitle } from "./alert"

interface MobileErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  componentName?: string
}

interface MobileErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class MobileErrorBoundary extends React.Component<MobileErrorBoundaryProps, MobileErrorBoundaryState> {
  constructor(props: MobileErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): MobileErrorBoundaryState {
    // Log mobile-specific error details
    console.error('Mobile rendering error:', {
      error: error.message,
      stack: error.stack,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });
    
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(`Mobile error in ${this.props.componentName || 'component'}:`, error, errorInfo)
    
    // Check for common mobile rendering issues
    if (error.message.includes('object with keys')) {
      console.error('Detected React error #31 - object rendering issue on mobile');
      console.error('Error details:', {
        componentStack: errorInfo?.componentStack,
        errorBoundary: this.props.componentName,
        timestamp: new Date().toISOString()
      });
    }
    
    if (error.message.includes('Objects are not valid as a React child')) {
      console.error('Detected React #31 error - invalid object rendering');
      console.error('Component:', this.props.componentName);
      console.error('Stack:', error.stack);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Alert variant="destructive" className="m-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Mobile Display Error</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <div className="text-sm">
              Something went wrong displaying this component on mobile.
              {this.props.componentName && (
                <span className="block font-medium">{this.props.componentName} failed to render</span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm" 
              onClick={this.handleReset}
              className="mt-2 self-start"
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )
    }

    return this.props.children
  }
}